/**
 * HOME SISTERS — the scroll driver (Kyle dictation, 2026-08-04; pruned
 * 2026-08-08 when the index card beat was cut).
 *
 * Does exactly two things, and deliberately nothing else:
 *
 *   1. MEASURES the rendered square and writes --eg-hs-cover, the ratio that
 *      turns it into a full-bleed frame. Once on load, again on resize.
 *   2. WRITES --eg-hs-p, the photograph beat's scroll progress, onto the
 *      section host on every animation frame the page scrolls.
 *
 * It used to write a third property, --eg-hs-cp, for the dotted-grid index card
 * that followed the photograph. Kyle cut that card on 2026-08-08 and the
 * property went with it; the `cardTrack` element it was measured from no longer
 * exists in the Liquid, so keeping the read would have made the whole driver
 * bail on a missing node and taken the photograph's resize down with it.
 *
 * Every threshold, distance and easing decision is in
 * section-eg-home-sisters.css against those two properties. This file has no
 * design values in it at all, which is the point: dialing the beat is a token
 * edit, never a JS edit.
 *
 * WHY THIS FILE EXISTS RATHER THAN `animation-timeline: scroll()`. The native
 * feature does all of the above with no script. It is not in release Firefox,
 * and Kyle uses Firefox. Measured on his own build, 2026-08-04, Firefox
 * 153.0.1: CSS.supports('animation-timeline', 'scroll()') === false and
 * 'ScrollTimeline' in window === false. Same call, and the same reasoning, as
 * eg-home-panel.js — which has the longer version of it.
 *
 * ONE THING HERE IS NOT AVAILABLE TO CSS AT ALL, rather than merely awkward.
 * --eg-hs-cover is max(window width, window height) ÷ the square's side: a
 * length divided by a length. calc() divides only by a NUMBER, so there is no
 * spelling of that expression in CSS however it is arranged. That is why the
 * measurement is here and not a token.
 *
 * MOTION PREFERENCE IS NOT CHECKED HERE, ON PURPOSE. Writing a number onto an
 * element is not motion. The CSS gates every animated declaration behind
 * `@media (prefers-reduced-motion: no-preference)`, so that media query is the
 * single arbiter — including when the user flips the OS setting mid-session,
 * which a JS-side check would miss without a listener nobody would remember to
 * add.
 *
 * FAIL-VISIBLE. `data-eg-hs-ready` is only set once the measurement succeeds.
 * If this script never runs, or throws, the CSS motion rules never match and
 * the section renders in its settled state — square in place, copy visible —
 * which is the same state the committed defaults of --eg-hs-p: 1 and
 * --eg-hs-cover: 1 produce. There is no state of this page in which the
 * photograph is the wrong size or the copy is invisible and stays that way.
 */
(function () {
  var host = document.querySelector('.eg-homesisters-host');
  if (!host) return;

  var stage = host.querySelector('[data-eg-hs-stage]');
  var slot = host.querySelector('[data-eg-hs-slot]');
  if (!stage || !slot) return;

  /* ---- 1) measure ----
     THE SLOT, NOT THE FRAME. The frame carries the transform this ratio feeds,
     so its own rect reports whatever it is currently scaled to — asking it how
     big the square is would be asking the output for the input. The slot is the
     untransformed grid cell beside it and exists for exactly this.

     `max()` of the two viewport axes because a square scaled to the LONGER edge
     covers the window on both. window.innerWidth counts the vertical scrollbar
     the page guarantees, so this over-covers by a few pixels — which is the
     safe direction: the alternative is a hairline of Vanilla down one edge of
     what is supposed to be a full-bleed photograph.

     Guarded against a zero width. A square with no width cannot produce a
     ratio, and Infinity in a transform would take the section off screen with
     nothing in the CSS to blame; leaving cover at its committed 1 renders the
     settled square instead. */
  function measure() {
    var side = slot.getBoundingClientRect().width;
    if (!side) return;
    var cover = Math.max(window.innerWidth, window.innerHeight) / side;
    host.style.setProperty('--eg-hs-cover', cover.toFixed(4));
  }

  /* ---- 2) drive ----
     Progress is measured off the TRACK — a plain `position: relative` box —
     and never off the sticky pin inside it. A sticky element's rect stops
     moving the moment it pins, which is precisely when this needs to start
     counting; and its offsetTop reports its stuck position rather than its
     flow position, so neither is usable here.

       travel = trackHeight - viewportHeight   the pinned scroll budget
       p      = -rect.top / travel             0 as the pin takes hold,
                                               1 at the end of the track

     Clamped, so p is 0 for the whole approach and 1 for anything past the
     track — which is what keeps the transform parked at its finished value for
     the rest of the page rather than running past it. */
  function progress(el) {
    var rect = el.getBoundingClientRect();
    var travel = rect.height - window.innerHeight;
    if (travel <= 0) return 1;
    var p = -rect.top / travel;
    return p < 0 ? 0 : p > 1 ? 1 : p;
  }

  /* rAF-coalesced. `scroll` fires far more often than the compositor paints,
     and every handler here ends in a style write — writing more than once per
     frame is pure waste, and on a trackpad it is a lot of waste. */
  var queued = false;

  function commit() {
    queued = false;
    host.style.setProperty('--eg-hs-p', progress(stage).toFixed(4));
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(commit);
  }

  /* `passive` because this never calls preventDefault — without it the browser
     has to wait for the handler before it can scroll, which is exactly the jank
     this whole file is trying not to introduce. */
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener(
    'resize',
    function () {
      measure();
      schedule();
    },
    { passive: true },
  );

  measure();
  commit();
  host.setAttribute('data-eg-hs-ready', '');

  /* THE SQUARE'S SIDE IS min(56rem, 62vh) AND NOTHING ABOUT IT WAITS ON A FONT,
     so the measurement above is already final. This last call is for the
     photograph itself: an <img> that decodes late does not change the slot, but
     a browser that has not yet applied the section's stylesheet when a deferred
     script runs would. `document.fonts.ready` is the cheapest "layout has
     settled" signal available without a ResizeObserver, and it costs one extra
     measurement on a promise that has usually already resolved. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      measure();
      schedule();
    });
  }
})();
